// The Panel Spec tab of the Device Specification Utility: the master catalog's panel specs (Type
// Panel), and this database's panels with the Index Sheet each uses. A panel's Index Sheet can be set
// to a Panel-type sheet this database already has; a catalog panel spec gets here by a pull first.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Pencil } from 'lucide-react';
import { dsSpecApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { ConfirmModal } from './CatalogModals';
import SpecBrowser from './SpecBrowser';
import { errText } from './deviceSpec';
import StuiSelect from '../../components/common/StuiSelect';

const SEARCH_FIELDS = ['panel', 'description', 'indexSheet'];

function SetPanelSheetModal({ panel, sheets, busy, onSet, onClose }) {
  const [sheetId, setSheetId] = useState(panel.sheetIsPanelSpec ? String(panel.indexSheetId) : '');
  const picked = sheets.find(s => String(s.id) === sheetId);
  return (
    <ConfirmModal title={`Index Sheet for ${panel.panel}`} confirmLabel="Set" busy={busy}
      confirmDisabled={!picked || picked.id === panel.indexSheetId} onConfirm={() => onSet(picked)} onClose={onClose}>
      <div className="stui-field-group stui-mb-md">
        <label htmlFor="pss-sheet">Panel spec</label>
        <StuiSelect id="pss-sheet" className="stui-field-input" value={sheetId} disabled={busy} onChange={e => setSheetId(e.target.value)}>
          <option value="">Pick a Panel-type Index Sheet…</option>
          {sheets.map(s => <option key={s.id} value={s.id}>{`${s.indexSheet}${s.description ? ` — ${s.description}` : ''}${s.status && s.status !== 'Active' ? ` (${s.status})` : ''}`}</option>)}
        </StuiSelect>
      </div>
      <p className="stui-hint">
        {`Currently ${panel.indexSheet ?? 'none'}${panel.indexSheet && !panel.sheetIsPanelSpec ? ' (not a panel spec)' : ''}. ` +
          'Only Panel-type sheets already in this database are offered; pull a catalog panel spec in first.'}
      </p>
    </ConfirmModal>
  );
}

export default function PanelSpecTab({ dbSlug, onOpenInBuilder }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = useMemo(() => ({ headers: { 'X-Changed-By': currentUser?.email || '' } }), [currentUser]);
  const [data, setData] = useState({ panels: [], sheets: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dsSpecApi.getPanels();
      setData(res.data ?? { panels: [], sheets: [] });
    } catch (e) { notify.error(errText(e, 'Failed to load the panels.')); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => applySearch(data.panels, search, SEARCH_FIELDS), [data.panels, search]);

  const setSheet = async sheet => {
    setBusy(true);
    try {
      await dsSpecApi.setPanelSheet(editing.id, sheet.id, editing.indexSheetId ?? null, auditCfg);
      notify.success(`${editing.panel} now uses ${sheet.indexSheet}.`);
      setEditing(null);
      await load();
    } catch (e) { notify.error(errText(e, 'Could not set the Index Sheet.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="stui-tab-body">
      <div className="stui-subsection-divider">Master catalog panel specs</div>
      <SpecBrowser type="Panel" dbSlug={dbSlug} onOpenInBuilder={onOpenInBuilder} />

      <div className="stui-subsection-divider stui-mt-lg">This database's panels</div>
      <div className="stui-page-header">
        <div className="stui-row-8">
          <span className="stui-text-sm-muted">
            {`${shown.length === data.panels.length ? data.panels.length : `${shown.length} of ${data.panels.length}`} panels; ${data.sheets.length} panel spec${data.sheets.length === 1 ? '' : 's'} in this database`}
          </span>
        </div>
        <div className="stui-row-8">
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
            <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search panels..." title={SEARCH_HINT} />
          </div>
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>
      <div className="stui-data-grid-container stui-scroll-cap stui-mt-sm">
        <table className="stui-data-grid stui-data-grid-auto">
          <thead><tr><th>Panel</th><th>Description</th><th>Index Sheet</th><th /></tr></thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={4} className="stui-td-empty">{loading ? 'Loading…' : data.panels.length === 0 ? 'This database has no panels.' : 'No matching results'}</td></tr>
            )}
            {shown.map(p => (
              <tr key={p.id}>
                <td className="stui-mono">{p.panel}</td>
                <td>{p.description}</td>
                <td className={p.sheetIsPanelSpec ? 'stui-mono' : 'stui-td-muted'} title={p.indexSheet && !p.sheetIsPanelSpec ? `Not a panel spec (${p.indexSheetType || 'blank type'})` : ''}>
                  {p.indexSheet ?? ''}
                </td>
                <td>
                  <button className="stui-btn stui-btn-secondary stui-btn-sm" disabled={data.sheets.length === 0}
                    onClick={() => setEditing(p)} title={data.sheets.length === 0 ? 'This database has no Panel-type Index Sheets' : 'Set the panel spec'}>
                    <Pencil size={14} /> Set
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <SetPanelSheetModal panel={editing} sheets={data.sheets} busy={busy} onSet={setSheet} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
